import React from "react";
import {
  Box,
  Card,
  Typography,
  Switch,
  Grid,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TagIcon from "@mui/icons-material/Tag";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import HubIcon from "@mui/icons-material/Hub";
import StorageIcon from "@mui/icons-material/Storage";
import BugReportIcon from "@mui/icons-material/BugReport";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LinkIcon from "@mui/icons-material/Link";
import { useModules } from "../../ModuleContext";
import { useKeycloak } from "../../KeycloakProvider";
import { isOrgAdmin } from "../../auth/rbac";
import getEnv from "../../config";

const MODULE_INFO = [
  { key: "crawlers", label: "Web Crawlers", desc: "Dark/surface web crawling", icon: SearchRoundedIcon },
  { key: "social_crawlers", label: "Social Crawlers", desc: "Reddit monitoring", icon: TagIcon },
  { key: "cti_gathering", label: "CTI Gathering", desc: "Automated threat intel collection", icon: StorageIcon },
  { key: "extractor", label: "CTI Extractor", desc: "IoC extraction from sources", icon: BugReportIcon },
  { key: "ontologies", label: "Ontologies", desc: "Knowledge graph management", icon: AccountTreeIcon },
  { key: "ids", label: "IDS", desc: "Intrusion detection integration", icon: SecurityRoundedIcon },
  { key: "taxonomies", label: "Taxonomies", desc: "Classification management", icon: HubIcon },
  { key: "correlations", label: "Correlations", desc: "Event correlation engine", icon: LinkIcon },
];

const AUTH_ENABLED = getEnv("REACT_APP_AUTH_ENABLED") === "true";

const ModuleSettings = () => {
  const { modules, available, toggleModule } = useModules();
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const canEdit = !AUTH_ENABLED || isOrgAdmin(keycloak);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Enable or disable platform modules. Changes take effect immediately.
      </Typography>

      <Grid container spacing={2}>
        {MODULE_INFO.filter(({ key }) => available.includes(key)).map(
          ({ key, label, desc, icon: Icon }) => {
            const enabled = modules[key] ?? true;
            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "box-shadow 0.2s ease, opacity 0.2s ease",
                    opacity: enabled ? 1 : 0.6,
                    "&:hover": enabled
                      ? { boxShadow: `0 4px 24px ${alpha(accent, 0.18)}` }
                      : undefined,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: 1,
                        backgroundColor: enabled
                          ? alpha(accent, 0.12)
                          : "action.disabledBackground",
                        color: enabled ? accent : "text.disabled",
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {desc}
                      </Typography>
                    </Box>
                    <Switch
                      checked={enabled}
                      onChange={() => toggleModule(key, !enabled)}
                      disabled={!canEdit}
                      size="small"
                      color="secondary"
                    />
                  </Box>
                </Card>
              </Grid>
            );
          }
        )}
      </Grid>
    </Box>
  );
};

export default ModuleSettings;
